import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * AnimatedFlameIcon - An animated flame icon component for like buttons
 * 
 * Features:
 * - Bottom-to-top fill animation when liked
 * - Top-to-bottom drain animation when unliked
 * - Spark burst effect on like
 * - Uses official Burnt logo flame shape
 * - Hardware-accelerated animations
 * - Responsive sizing and theming
 */
const AnimatedFlameIcon = ({ 
  isLiked = false, 
  className = 'w-4 h-4' 
}) => {
  // Determine spark size based on icon size
  const getSparkSize = (className) => {
    if (className.includes('w-3') || className.includes('h-3')) return 'w-0.5 h-0.5';
    if (className.includes('w-5') || className.includes('h-5')) return 'w-1.5 h-1.5';
    return 'w-1 h-1'; // default for w-4 h-4
  };

  const sparkSize = getSparkSize(className);
  // Official Burnt logo flame path data
  const flamePath = "M58.5892 0C116.09 3.5 119 56.5 115.5 111.5C115.087 118 128.089 118.5 136.089 88.5C136.089 88.5 156.373 123.13 150.089 149C145.206 169.103 139.437 185.34 121.589 199C101.64 214.268 83.1878 217.098 58.5892 212C38.6194 207.861 25.9227 201.742 13.5892 185.5C1.35797 169.392 -0.474445 155.718 0.0892185 135.5C1.01798 102.187 23.8487 89.4744 44.0902 63C50.589 54.5001 54.0004 54.5 60.0902 40.5C65.3555 28.3955 58.5892 0 58.5892 0ZM79.9349 69C79.9349 69 52.5444 100.257 39.5004 124.5C32.9286 136.714 15.9087 164.459 43.9984 179C53.3429 183.837 69.4142 185.171 79.9349 184.998C94.0225 184.767 105.111 183.396 113.668 172.203C120.328 163.493 120.814 155.805 120.067 144.866C119.376 134.76 104.772 127.999 104.772 127.999C104.715 127.955 97.958 122.735 87.9056 115.785C58.9254 95.7489 79.9349 69 79.9349 69Z";
  
  // Generate unique mask ID for this component instance (memoized)
  const maskId = useMemo(() => 
    `flame-mask-${Math.random().toString(36).substr(2, 9)}`, 
    []
  );

  // Animation variants for fill/drain effect (memoized)
  const fillVariants = useMemo(() => ({
    unliked: { 
      y: '100%',
      transition: { 
        duration: 0.4, 
        ease: 'easeInOut' 
      }
    },
    liked: { 
      y: '0%',
      transition: { 
        duration: 0.6, 
        ease: 'easeInOut' 
      }
    }
  }), []);

  // Spark trajectory coordinates (memoized)
  const sparkTrajectories = useMemo(() => [
    { x: -8, y: -12 },   // Top-left
    { x: 0, y: -15 },    // Top-center  
    { x: 8, y: -12 },    // Top-right
    { x: -6, y: -8 },    // Mid-left
    { x: 6, y: -8 }      // Mid-right
  ], []);

  // Spark animation variants (memoized)
  const sparkVariants = useMemo(() => ({
    hidden: { 
      opacity: 0, 
      scale: 0,
      x: 0,
      y: 0
    },
    visible: (i) => ({
      opacity: [0, 1, 0],
      scale: [0, 1, 0.8],
      x: sparkTrajectories[i].x,
      y: sparkTrajectories[i].y,
      transition: {
        duration: 0.5,
        delay: 0.2 + (i * 0.05), // Staggered timing
        ease: 'easeOut'
      }
    })
  }), [sparkTrajectories]);

  return (
    <div className="relative" style={{ willChange: 'transform' }}>
      <svg 
        viewBox="0 0 152 215" 
        className={className}
        style={{ 
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)' // Force hardware acceleration
        }}
      >
        <defs>
          {/* Mask for fill/drain animation */}
          <mask id={maskId}>
            <motion.rect
              width="100%"
              height="100%"
              fill="white"
              variants={fillVariants}
              animate={isLiked ? 'liked' : 'unliked'}
              initial="unliked"
            />
          </mask>
        </defs>
        
        {/* Background: Gray outline flame */}
        <path 
          d={flamePath}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-gray-500 dark:text-gray-400"
        />
        
        {/* Foreground: Purple filled flame with mask */}
        <path 
          d={flamePath}
          fill="currentColor"
          className="text-purple-600"
          mask={`url(#${maskId})`}
        />
      </svg>

      {/* Spark burst container */}
      <div 
        className="absolute top-0 left-1/2 transform -translate-x-1/2"
        style={{ willChange: 'transform' }}
      >
        <AnimatePresence>
          {isLiked && sparkTrajectories.map((_, index) => (
            <motion.div
              key={`spark-${index}`}
              className={`absolute ${sparkSize} bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full`}
              variants={sparkVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              custom={index}
              style={{
                boxShadow: '0 0 6px rgba(251, 191, 36, 0.9), 0 0 12px rgba(249, 115, 22, 0.6)',
                willChange: 'transform, opacity',
                backfaceVisibility: 'hidden'
              }}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

AnimatedFlameIcon.propTypes = {
  isLiked: PropTypes.bool,
  className: PropTypes.string
};

export default AnimatedFlameIcon;